import React, { useRef, useEffect } from 'react';
import { FiCopy, FiVolume2, FiMessageSquare } from 'react-icons/fi';
import { RiRobot2Line } from 'react-icons/ri';
import toast from 'react-hot-toast';
import { PromptSuggestions } from './PromptSuggestions';
import { MessageInput } from './MessageInput';
import './ChatPanel.css';

export function ChatPanel({
  chat,
  isLoading,
  onSendMessage,
  onReplaySpeech,
  inputMessage,
  setInputMessage,
  isListening,
  onToggleVoice,
  voiceTranscript,
}) {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat, isLoading]);

  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-panel">
      {/* Header */}
      <div className="chat-panel-header">
        <div className="header-title-group">
          <FiMessageSquare className="panel-header-icon" />
          Conversation
        </div>
        <span className="message-count-badge">
          {chat.length} {chat.length === 1 ? 'message' : 'messages'}
        </span>
      </div>

      {/* Messages */}
      <div className="chat-messages-container">
        {chat.length === 0 ? (
          <div className="empty-chat-state">
            <div className="empty-icon-wrapper">
              <RiRobot2Line />
            </div>
            <h3 className="empty-title">How can I assist you today?</h3>
            <p className="empty-description">
              Ask about O7 Services industrial training programs, enterprise software products, or IT solutions.
            </p>
            <div className="empty-suggestions">
              <PromptSuggestions onSelectPrompt={onSendMessage} disabled={isLoading} />
            </div>
          </div>
        ) : (
          <div className="message-list">
            {chat.map((msg, idx) => {
              const isUser = msg.sender === 'user';
              return (
                <div key={idx} className={`message-row ${isUser ? 'user-row' : 'assistant-row'}`}>
                  {!isUser && (
                    <div className="assistant-avatar-icon">
                      <RiRobot2Line />
                    </div>
                  )}

                  <div className={`message-bubble ${isUser ? 'user-bubble' : 'assistant-bubble'}`}>
                    <div className="message-meta">
                      <span className="author-name">{isUser ? 'You' : 'O7 Assistant'}</span>
                      {msg.timestamp && <span className="timestamp">{formatTimestamp(msg.timestamp)}</span>}
                    </div>

                    <div className="message-text">{msg.text}</div>

                    {!isUser && (
                      <div className="message-actions">
                        <button type="button" className="action-btn" onClick={() => handleCopyMessage(msg.text)} title="Copy">
                          <FiCopy /> Copy
                        </button>
                        <button type="button" className="action-btn" onClick={() => onReplaySpeech(msg.text)} title="Speak">
                          <FiVolume2 /> Speak
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <div className="message-row assistant-row">
                <div className="assistant-avatar-icon"><RiRobot2Line /></div>
                <div className="message-bubble assistant-bubble loading-bubble">
                  <span className="loading-dot" />
                  <span className="loading-dot" />
                  <span className="loading-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="chat-panel-footer">
        {chat.length > 0 && (
          <PromptSuggestions onSelectPrompt={onSendMessage} disabled={isLoading} />
        )}
        <MessageInput
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          onSendMessage={onSendMessage}
          isLoading={isLoading}
          isListening={isListening}
          onToggleVoice={onToggleVoice}
          voiceTranscript={voiceTranscript}
        />
      </div>
    </div>
  );
}
